import { Box, Typography, useMediaQuery } from '@mui/material'
import GoBackButton from 'components/GoBackButton/GoBackButton'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { Form, Formik } from 'formik'
import {
  useGetCategoryTrees,
  usePostSearchProducts,
} from 'hooks/useSearchConfig'
import { contentLanguages } from 'lib/languages'
import theme from 'lib/theme'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ConfigAdjustment } from './ConfigAdjustment'
import { ProductTable } from './ProductTable'
import { ResponsiveBoxWrapper } from './SearchConfig.styled'
import { SettingsForm } from './SettingsForm'

export type DefaultConfigFields = {
  gtin: number
  categoriesParentName: number
  categoriesName: number
  description: number
  functionalName: number
  keywords: number
  brand: number
  name: number
  nameKeyword: number
  minScore: number
}

export type DefaultConfig = {
  fields: DefaultConfigFields
  minScore: number
}

export interface ConfigFieldTypes extends DefaultConfigFields {
  store: { label: string; value: string }
  categoryTree: string
  search: string
  contentLanguage: string
}

type Settings = {
  tree: string
  search?: string
  lang: string
}

export const SearchConfig = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { mutateAsync, isLoading, error: mutateError } = usePostSearchProducts()
  const {
    isLoading: isLoadingCategories,
    error,
    categoryTrees,
  } = useGetCategoryTrees()

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [showOverlay, setShowOverlay] = useState(true)

  const [defaultBoostConfig, setDefaultBoostConfig] = useState<DefaultConfig>()
  const [boostConfig, setBoostConfig] = useState<DefaultConfig>()
  const [settings, setSettings] = useState<Settings | null>(null)
  const [selectedPlace, setSelectedPlace] = useState<{
    label: string
    value: string
  } | null>(null)
  const [productData, setProductData] = useState([])
  const [pagination, setPagination] = useState()
  const [page, setPage] = useState(1)

  useEffect(() => {
    const findConfigValues = async () => {
      const values = {
        lang: contentLanguages[0],
        tree: 'online',
        meta: { debug: true },
      }
      await mutateAsync(values, {
        onSuccess: (response) => {
          setBoostConfig(response.data.meta.debug.boostParams.default)
          setDefaultBoostConfig(response.data.meta.debug.boostParams.default)
        },
      })
    }
    findConfigValues()
  }, [])

  const handleSubmitConfig = async (values: ConfigFieldTypes) => {
    setSettings({
      tree: values.categoryTree,
      search: values.search,
      lang: values.contentLanguage,
    })
    setSelectedPlace(values.store)
    const configValues = {
      lang: values.contentLanguage,
      tree: values.categoryTree,
      store_id: values.store.value,
      search_phrase: values.search,
      meta: { debug: true, pagination: { page } },
      boosts: {
        fields: {
          name: values.name,
          name_keyword: values.nameKeyword,
          keywords: values.keywords,
          brand: values.brand,
          description: values.description,
          functional_name: values.functionalName,
          categories_name: values.categoriesName,
          categories_parent_name: values.categoriesParentName,
          gtin: values.gtin,
        },
        min_score: values.minScore,
      },
    }
    await mutateAsync(configValues, {
      onSuccess: (response) => {
        setBoostConfig(response.data.meta.debug.requestParams.boosts)
        setProductData(response.data.data.products)
        setPagination(response.data.meta.pagination)
      },
    })
  }

  const initialValues = {
    gtin: boostConfig?.fields.gtin || 1,
    categoriesParentName: boostConfig?.fields.categoriesParentName || 1,
    categoriesName: boostConfig?.fields.categoriesName || 1,
    description: boostConfig?.fields.description || 1,
    functionalName: boostConfig?.fields.functionalName || 1,
    keywords: boostConfig?.fields.keywords || 1,
    brand: boostConfig?.fields.brand || 1,
    name: boostConfig?.fields.name || 1,
    nameKeyword: boostConfig?.fields.nameKeyword || 1,
    minScore: boostConfig?.minScore || 1,
    store: selectedPlace || { label: '', value: '' },
    categoryTree: settings?.tree || '',
    contentLanguage: settings?.lang || contentLanguages[0],
    search: settings?.search || '',
  }

  if (error || mutateError) return <h1>Error</h1>
  if (!boostConfig || isLoadingCategories) return <LoadingIndicator />
  return (
    <Box width={isMobile ? '100vw' : 'auto'}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={(values) => handleSubmitConfig(values)}>
        {({ setFieldValue, values, submitForm }) => (
          <Form>
            <Box
              display='flex'
              height='100vh'
              sx={{
                overflowX: isMobile ? 'auto' : 'hidden',
                width: isMobile ? '100%' : 'calc(100vw - 40px)',
              }}>
              <Box
                sx={{
                  padding: `${theme.spacing(2)} ${theme.spacing(2)}`,
                  flexGrow: 1,
                  overflowX: 'hidden',
                }}>
                {isMobile && <GoBackButton onClick={() => navigate('/')} />}
                <Box display='flex' justifyContent='space-between'>
                  <Typography variant='h1'>
                    {t('searchConfigurationTool')}
                  </Typography>
                </Box>
                <SettingsForm
                  setShowOverlay={setShowOverlay}
                  setFieldValue={setFieldValue}
                  values={values}
                  categoryTrees={categoryTrees}
                  submitForm={submitForm}
                  setPage={setPage}
                  setSelectedPlace={setSelectedPlace}
                />
                {pagination && productData && (
                  <ProductTable
                    isLoading={isLoading}
                    submitForm={submitForm}
                    values={values}
                    productData={productData}
                    pagination={pagination}
                    page={page}
                    setPage={setPage}
                  />
                )}
              </Box>
              <ResponsiveBoxWrapper showOverlay={showOverlay}>
                {isMobile && (
                  <GoBackButton onClick={() => setShowOverlay(false)} />
                )}
                <ConfigAdjustment
                  handleSubmitConfig={handleSubmitConfig}
                  defaultBoostConfig={defaultBoostConfig}
                  setFieldValue={setFieldValue}
                  values={values}
                  setBoostConfig={setBoostConfig}
                />
              </ResponsiveBoxWrapper>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}
